(function() {
  'use strict';

  const SELECTORS = {
    CAROUSEL: 's-video-reviews-carousel',
    SLIDER: '.js-video-reviews-carousel__slider',
    VIDEO_WRAPPER: '.js-video-reviews-carousel__video-wrapper',
    VIDEO: '.js-video-reviews-carousel__video',
    PREVIEW: '.js-video-reviews-carousel__preview',
    PLAY_BUTTON: '.js-video-reviews-carousel__play-button',
    DOTS: '.s-video-reviews-carousel__dot',
    CARDS: '.s-video-reviews-carousel__card'
  };

  const CLASSES = {
    LOADED: 'is-loaded',
    HIDDEN: 'is-hidden',
    PLAYING: 'is-playing',
    ACTIVE: 'active'
  };

  class VideoReviewsCarousel extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.dots = [];
      this.cards = [];
      this.videoHandlers = new Map();
      this.isScrolling = false;
      this.scrollTimeout = null;
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    init() {
      this.slider = this.querySelector(SELECTORS.SLIDER);
      if (!this.slider) return;

      this.dots = Array.from(this.querySelectorAll(SELECTORS.DOTS));
      this.cards = Array.from(this.querySelectorAll(SELECTORS.CARDS));

      if (this.dots.length === 0 || this.cards.length === 0) return;

      this.initCarousel();
      this.initVideoControls();
      this.classList.add(CLASSES.LOADED);
    }

    initCarousel() {
      // Setup scroll behavior
      this.slider.style.scrollBehavior = 'smooth';
      this.slider.style.scrollSnapType = 'x mandatory';

      // Add scroll event listener with throttling
      this.slider.addEventListener('scroll', () => {
        if (!this.isScrolling) {
          requestAnimationFrame(() => {
            this.updateActiveDot();
            this.isScrolling = false;
          });
          this.isScrolling = true;
        }
      }, { passive: true });

      // Also listen for scrollend event if available (for better dot updates)
      if ('onscrollend' in this.slider) {
        this.slider.addEventListener('scrollend', () => {
          this.updateActiveDot();
        }, { passive: true });
      }

      // Add click handlers to dots
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.scrollToItem(index);
        });
      });

      // Handle resize to recalculate positions
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          this.updateActiveDot();
        }, 250);
      }, { passive: true });

      // Initial dot update
      setTimeout(() => {
        this.updateActiveDot();
      }, 100);
    }

    // Function to get item width including gap
    getItemWidth() {
      if (this.cards.length === 0) return 0;
      
      const card = this.cards[0];
      const computedStyle = window.getComputedStyle(card);
      const cardWidth = card.offsetWidth;
      const gap = parseInt(window.getComputedStyle(this.slider).gap) || 
                  parseInt(window.getComputedStyle(this.slider).columnGap) || 
                  20;
      return cardWidth + gap;
    }

    // Function to update active dot based on visible item
    updateActiveDot() {
      if (!this.slider || this.cards.length === 0) return;

      const scrollLeft = this.slider.scrollLeft;
      const sliderWidth = this.slider.clientWidth;
      const sliderCenter = scrollLeft + sliderWidth / 2;
      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
      
      let activeIndex = 0;
      let closestDistance = Infinity;
      
      // Check if we're at the beginning
      if (scrollLeft <= 5) {
        activeIndex = 0;
      }
      // Check if we're at the end
      else if (scrollLeft >= maxScroll - 5) {
        activeIndex = this.cards.length - 1;
      }
      // Otherwise, find the card closest to center
      else {
        this.cards.forEach((card, index) => {
          const cardOffsetLeft = card.offsetLeft;
          const cardWidth = card.offsetWidth;
          const cardCenter = cardOffsetLeft + cardWidth / 2;
          const distance = Math.abs(sliderCenter - cardCenter);
          
          if (distance < closestDistance) {
            closestDistance = distance;
            activeIndex = index;
          }
        });
      }
      
      this.dots.forEach((dot, index) => {
        dot.classList.toggle(CLASSES.ACTIVE, index === activeIndex);
      });
    }

    // Function to scroll to specific item
    scrollToItem(index) {
      const targetCard = this.cards[index];
      if (!targetCard || !this.slider) return;
      
      // Get the card's offsetLeft relative to the slider
      const cardOffsetLeft = targetCard.offsetLeft;
      const cardWidth = targetCard.offsetWidth;
      const sliderWidth = this.slider.clientWidth;
      
      // Calculate the position to center the card
      const scrollPosition = cardOffsetLeft - (sliderWidth / 2) + (cardWidth / 2);
      
      // Ensure we don't scroll beyond boundaries
      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
      const finalScrollPosition = Math.max(0, Math.min(scrollPosition, maxScroll));
      
      this.slider.scrollTo({
        left: finalScrollPosition,
        behavior: 'smooth'
      });
      
      // Update dot after scroll completes
      setTimeout(() => {
        this.updateActiveDot();
      }, 400);
    }

    initVideoControls() {
      const playButtons = this.querySelectorAll(SELECTORS.PLAY_BUTTON);
      
      playButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.handlePlayClick(button);
        });
      });

      // Handle video events
      const videos = this.querySelectorAll(SELECTORS.VIDEO);
      videos.forEach(video => {
        this.setupVideoEvents(video);
      });
    }

    setupVideoEvents(video) {
      const wrapper = video.closest(SELECTORS.VIDEO_WRAPPER);
      if (!wrapper) return;

      const preview = wrapper.querySelector(SELECTORS.PREVIEW);
      const playButton = wrapper.querySelector(SELECTORS.PLAY_BUTTON);

      // Ensure video is muted initially (preview without sound)
      video.muted = true;

      const handlePlay = () => {
        if (preview) preview.classList.add(CLASSES.HIDDEN);
        if (playButton) playButton.classList.add(CLASSES.HIDDEN);
        video.classList.add(CLASSES.PLAYING);
        video.muted = false; // Unmute when playing
      };

      const handlePause = () => {
        if (video.currentTime === 0 || video.ended) {
          if (preview) preview.classList.remove(CLASSES.HIDDEN);
          if (playButton) playButton.classList.remove(CLASSES.HIDDEN);
          video.classList.remove(CLASSES.PLAYING);
          video.muted = true; // Mute when paused
        }
      };

      const handleEnded = () => {
        if (preview) preview.classList.remove(CLASSES.HIDDEN);
        if (playButton) playButton.classList.remove(CLASSES.HIDDEN);
        video.classList.remove(CLASSES.PLAYING);
        video.muted = true;
        video.currentTime = 0;
      };

      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);
      video.addEventListener('ended', handleEnded);

      // Store handlers for cleanup
      this.videoHandlers.set(video, { handlePlay, handlePause, handleEnded });
    }

    handlePlayClick(button) {
      const wrapper = button.closest(SELECTORS.VIDEO_WRAPPER);
      if (!wrapper) return;

      const video = wrapper.querySelector(SELECTORS.VIDEO);
      if (!video) return;

      // Unmute and play
      video.muted = false;
      video.play().catch(error => {
        console.error('Error playing video:', error);
      });
    }

    cleanup() {
      // Remove video event listeners
      this.videoHandlers.forEach((handlers, video) => {
        video.removeEventListener('play', handlers.handlePlay);
        video.removeEventListener('pause', handlers.handlePause);
        video.removeEventListener('ended', handlers.handleEnded);
      });
      this.videoHandlers.clear();

      // Clear timeouts
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
    }
  }

  // Register custom element
  if (!customElements.get(SELECTORS.CAROUSEL)) {
    customElements.define(SELECTORS.CAROUSEL, VideoReviewsCarousel);
  }
})();
